<template>
  <div class="page-list" :class="listClasses" :style="listStyle">
    <!-- Drag handle slot for edit mode -->
    <slot name="drag-handle" />
    <!-- List items -->
    <slot />
  </div>
</template>

<script>
/**
 * ListNode - A flex container for link items (groups, campaigns, etc.)
 *
 * Items flow horizontally and wrap like inline-block elements.
 * Unlike Row/Col which is for page layout, ListNode is for
 * collections of similar items that should flow naturally.
 *
 * Example usage:
 * - list → groupLink, groupLink, groupLink, ...
 * - list → campaignLink, campaignLink, ...
 */
export default {
  name: 'ListNode',
  props: {
    nodeId: String,
    editMode: Boolean,
    // Gap between items in pixels
    gap: { type: [String, Number], default: 16 },
    // Item sizing
    itemWidth: { type: String, default: 'auto' },
    itemMinWidth: { type: String, default: '200px' },
    itemMaxWidth: { type: String, default: '280px' },
    // Responsive item widths (optional overrides)
    itemWidthSm: { type: String, default: null },
    itemWidthMd: { type: String, default: null },
    itemWidthLg: { type: String, default: null },
    // Alignment
    justify: {
      type: String,
      default: 'start',
      validator: (v) =>
        [
          'start',
          'center',
          'end',
          'space-between',
          'space-around',
          'space-evenly',
        ].includes(v),
    },
    align: {
      type: String,
      default: 'stretch',
      validator: (v) =>
        ['start', 'center', 'end', 'stretch', 'baseline'].includes(v),
    },
    // Custom class
    className: { type: String, default: '' },
  },
  computed: {
    listClasses() {
      return [
        this.className,
        `page-list--align-${this.align}`,
        `page-list--justify-${this.justify}`,
      ];
    },
    listStyle() {
      const gapValue =
        typeof this.gap === 'number' ? `${this.gap}px` : this.gap;

      const style = {
        '--page-list-gap': gapValue,
        '--page-list-item-width': this.itemWidth,
        '--page-list-item-min-width': this.itemMinWidth,
        '--page-list-item-max-width': this.itemMaxWidth,
      };

      // Responsive item widths
      if (this.itemWidthSm) {
        style['--page-list-item-width-sm'] = this.itemWidthSm;
      }
      if (this.itemWidthMd) {
        style['--page-list-item-width-md'] = this.itemWidthMd;
      }
      if (this.itemWidthLg) {
        style['--page-list-item-width-lg'] = this.itemWidthLg;
      }

      return style;
    },
  },
};
</script>

<style lang="less">
// Breakpoint values (matching Vuetify defaults)
@screen-sm: 600px;
@screen-md: 960px;
@screen-lg: 1280px;
@screen-xl: 1920px;

.page-list {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: var(--page-list-gap, 16px);

  // Alignment variants
  &--align-start {
    align-items: flex-start;
  }
  &--align-center {
    align-items: center;
  }
  &--align-end {
    align-items: flex-end;
  }
  &--align-stretch {
    align-items: stretch;
  }
  &--align-baseline {
    align-items: baseline;
  }

  // Justify variants
  &--justify-start {
    justify-content: flex-start;
  }
  &--justify-center {
    justify-content: center;
  }
  &--justify-end {
    justify-content: flex-end;
  }
  &--justify-space-between {
    justify-content: space-between;
  }
  &--justify-space-around {
    justify-content: space-around;
  }
  &--justify-space-evenly {
    justify-content: space-evenly;
  }
}

// Make intermediate wrapper elements transparent to flexbox
// This allows items to be direct flex children through PageNodeWrapper
.page-list {
  > .page-node-wrapper,
  > .page-node-children {
    display: contents;
  }
}

// Direct children of list get item sizing
.page-list > .page-node-wrapper > *,
.page-list > .page-node-children > * {
  flex: 0 1 var(--page-list-item-width, auto);
  min-width: var(--page-list-item-min-width, 200px);
  max-width: var(--page-list-item-max-width, 280px);
  box-sizing: border-box;

  // Responsive widths
  @media (min-width: @screen-sm) {
    &[style*='--page-list-item-width-sm'] {
      flex-basis: var(--page-list-item-width-sm);
    }
  }
  @media (min-width: @screen-md) {
    &[style*='--page-list-item-width-md'] {
      flex-basis: var(--page-list-item-width-md);
    }
  }
  @media (min-width: @screen-lg) {
    &[style*='--page-list-item-width-lg'] {
      flex-basis: var(--page-list-item-width-lg);
    }
  }
}

// ==============================================
// EDIT MODE: Flex-aware drag and drop
// ==============================================
// In edit mode, we need SortableJS to have real bounding boxes.
// The vue-draggable (page-node-children) becomes a flex container
// that mimics the list's layout.

.page-renderer--editing .page-list {
  // The vue-draggable container becomes a flex container
  > .page-node-children {
    display: flex;
    flex-wrap: wrap;
    align-items: inherit;
    justify-content: inherit;
    width: 100%;
    gap: var(--page-list-gap, 16px);
  }

  // Wrappers are flex items with list item sizing
  > .page-node-children > .page-node-wrapper {
    display: flex;
    box-sizing: border-box;
    flex: 0 1 var(--page-list-item-width, auto);
    min-width: var(--page-list-item-min-width, 200px);
    max-width: var(--page-list-item-max-width, 280px);

    // Responsive widths
    @media (min-width: @screen-sm) {
      .page-list[style*='--page-list-item-width-sm'] & {
        flex-basis: var(--page-list-item-width-sm);
      }
    }
    @media (min-width: @screen-md) {
      .page-list[style*='--page-list-item-width-md'] & {
        flex-basis: var(--page-list-item-width-md);
      }
    }
    @media (min-width: @screen-lg) {
      .page-list[style*='--page-list-item-width-lg'] & {
        flex-basis: var(--page-list-item-width-lg);
      }
    }
  }

  // Items inside wrappers fill their container
  > .page-node-children > .page-node-wrapper > * {
    flex: 1;
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  // Sortable feedback elements
  > .page-node-children > .sortable-ghost {
    opacity: 0.4;
    background: rgba(0, 0, 0, 0.05);
  }
  > .page-node-children > .sortable-drag {
    opacity: 0.8;
  }
}
</style>
